import React from 'react';

const Process = () => {
  const steps = [
    {
      id: 1,
      title: 'Discover',
      subtitle: 'Goals, constraints, risks',
      description: 'We analyze your business requirements, technical constraints, and potential risks to create a comprehensive project roadmap.',
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      color: 'bg-blue-500'
    },
    {
      id: 2,
      title: 'Design',
      subtitle: 'Wireframes, data flows, security',
      description: 'We create detailed wireframes, define data architecture, and establish security protocols to ensure a solid foundation.',
      icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z',
      color: 'bg-indigo-500'
    },
    {
      id: 3,
      title: 'Build',
      subtitle: 'Sprints, demos, feedback',
      description: 'We develop your solution using agile sprints with weekly demonstrations and continuous feedback integration.',
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      color: 'bg-purple-500'
    },
    {
      id: 4,
      title: 'Launch & Support',
      subtitle: 'Monitoring, updates, training',
      description: 'We deploy your solution with comprehensive monitoring, provide training, and offer ongoing support and updates.',
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      color: 'bg-green-500'
    }
  ];

  return (
    <section id="process" className="section-padding process-bg">
      <div className="container-xl">
        <div className="text-center mb-20">
          <p className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-4">
            Our Process
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl logo-font text-gray-900 mb-6">
            From Idea to Launch in 4 Clear Steps
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-slate-800 to-slate-600 mx-auto rounded-full mb-8"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our proven methodology ensures your project is delivered on time, on budget, and exceeds your expectations.
          </p>
        </div>

        {/* Desktop Process Flow */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Modern Process Steps */}
            <div className="grid grid-cols-4 gap-8 xl:gap-12 2xl:gap-16">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className="relative text-center group"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {/* Step Card */}
                  <div className="relative bg-white rounded-3xl p-8 xl:p-10 2xl:p-12 shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2 border border-gray-100">
                    {/* Step Number Badge */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`w-12 h-12 xl:w-14 xl:h-14 2xl:w-16 2xl:h-16 rounded-full flex items-center justify-center shadow-lg ${
                        index === 0 ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                        index === 1 ? 'bg-gradient-to-br from-indigo-500 to-indigo-600' :
                        index === 2 ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                        'bg-gradient-to-br from-green-500 to-green-600'
                      }`}>
                        <span className="text-white font-bold text-lg xl:text-xl 2xl:text-2xl">{step.id}</span>
                      </div>
                    </div>

                    {/* Icon */}
                    <div className={`w-20 h-20 xl:w-24 xl:h-24 2xl:w-28 2xl:h-28 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 ${
                      index === 0 ? 'bg-gradient-to-br from-blue-100 to-blue-200 group-hover:from-blue-500 group-hover:to-blue-600' :
                      index === 1 ? 'bg-gradient-to-br from-indigo-100 to-indigo-200 group-hover:from-indigo-500 group-hover:to-indigo-600' :
                      index === 2 ? 'bg-gradient-to-br from-purple-100 to-purple-200 group-hover:from-purple-500 group-hover:to-purple-600' :
                      'bg-gradient-to-br from-green-100 to-green-200 group-hover:from-green-500 group-hover:to-green-600'
                    }`}>
                      <svg
                        className={`w-10 h-10 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14 transition-colors duration-500 ${
                          index === 0 ? 'text-blue-800 group-hover:text-white' :
                          index === 1 ? 'text-indigo-800 group-hover:text-white' :
                          index === 2 ? 'text-purple-800 group-hover:text-white' :
                          'text-green-800 group-hover:text-white'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={step.icon}
                        />
                      </svg>
                    </div>

                    {/* Content */}
                    <div className="space-y-4">
                      <h3 className="text-xl xl:text-2xl font-bold text-gray-900 group-hover:text-slate-800 transition-colors duration-500">
                        {step.title}
                      </h3>
                      <p className="text-sm font-medium text-slate-600">
                        {step.subtitle}
                      </p>
                      <p className="text-gray-600 text-sm xl:text-base leading-relaxed">
                        {step.description}
                      </p>
                    </div>

                    {/* Connection Arrow (except for last item) */}
                    {index < steps.length - 1 && (
                      <div className="absolute top-1/2 -right-4 xl:-right-6 2xl:-right-8 transform -translate-y-1/2 z-10">
                        <div className={`w-8 h-8 xl:w-10 xl:h-10 2xl:w-12 2xl:h-12 rounded-full shadow-lg flex items-center justify-center border-2 transition-all duration-300 hover:scale-110 ${
                          index === 0 ? 'bg-gradient-to-br from-blue-500 to-indigo-500 border-blue-300' :
                          index === 1 ? 'bg-gradient-to-br from-indigo-500 to-purple-500 border-indigo-300' :
                          'bg-gradient-to-br from-purple-500 to-green-500 border-purple-300'
                        }`}>
                          <svg className="w-4 h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Process Flow */}
        <div className="lg:hidden space-y-8">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className="flex items-start space-x-4 group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Step Circle */}
              <div className={`flex-shrink-0 w-16 h-16 rounded-full ${step.color} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300`}>
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={step.icon}
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="flex-1 space-y-2">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors duration-300">
                    {step.title}
                  </h3>
                  <span className="text-xs font-medium text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                    Step {step.id}
                  </span>
                </div>
                <p className="text-sm font-medium text-indigo-600">
                  {step.subtitle}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Start Your Project?
            </h3>
            <p className="text-gray-600 mb-6">
              Our proven process ensures your project is delivered on time, on budget, and exceeds expectations.
            </p>
            <button
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Begin Discovery Phase
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
